/**
 * Госпошлина по имущественным искам в арбитражный суд.
 * Снимок config/reference/court_fee_arbitrazh.yaml.
 * fee = base + (amount - over) * percent / 100, затем min/max, округление до рубля HALF_UP.
 */
import { compareIso, formatRuDate, parseIsoDate, roundHalfUp, todayIso } from "@/lib/legal/calendar"

type FeeBracket = {
  upTo: number | null
  base: number
  over: number
  percent: number
  min: number | null
}

type FeeVersion = {
  effectiveFrom: string
  basis: string
  max: number | null
  brackets: readonly FeeBracket[]
}

const FEE_VERSIONS: readonly FeeVersion[] = [
  {
    effectiveFrom: "2024-09-08",
    basis: "НК РФ ст. 333.21 (ред. Федерального закона от 08.08.2024 № 176-ФЗ)",
    max: 10_000_000,
    brackets: [
      { upTo: 100_000, base: 10_000, over: 0, percent: 0, min: null },
      { upTo: 1_000_000, base: 10_000, over: 100_000, percent: 5, min: null },
      { upTo: 10_000_000, base: 55_000, over: 1_000_000, percent: 3, min: null },
      { upTo: 50_000_000, base: 325_000, over: 10_000_000, percent: 1, min: null },
      { upTo: null, base: 725_000, over: 50_000_000, percent: 0.5, min: null },
    ],
  },
  {
    effectiveFrom: "2005-01-01",
    basis: "НК РФ ст. 333.21 (ред. до 08.09.2024)",
    max: 200_000,
    brackets: [
      { upTo: 100_000, base: 0, over: 0, percent: 4, min: 2_000 },
      { upTo: 200_000, base: 4_000, over: 100_000, percent: 3, min: null },
      { upTo: 1_000_000, base: 7_000, over: 200_000, percent: 2, min: null },
      { upTo: 2_000_000, base: 23_000, over: 1_000_000, percent: 1, min: null },
      { upTo: null, base: 33_000, over: 2_000_000, percent: 0.5, min: null },
    ],
  },
]

export type CourtFeeResult = {
  claimAmount: number
  fee: number
  basis: string
  effectiveFrom: string
}

const VERSIONS: readonly FeeVersion[] = [...FEE_VERSIONS].sort((left, right) =>
  compareIso(right.effectiveFrom, left.effectiveFrom),
)

function versionOn(on: string): FeeVersion {
  parseIsoDate(on)
  for (const version of VERSIONS) {
    if (compareIso(version.effectiveFrom, on) <= 0) return version
  }
  throw new Error(`Нет шкалы госпошлины на дату ${formatRuDate(on)}`)
}

export function courtFee(claimAmount: number, on: string = todayIso()): CourtFeeResult {
  if (!(claimAmount > 0) || !Number.isFinite(claimAmount)) {
    throw new Error("Цена иска должна быть больше нуля")
  }

  const version = versionOn(on)
  let raw: number | null = null
  for (const bracket of version.brackets) {
    if (bracket.upTo === null || claimAmount <= bracket.upTo) {
      raw = bracket.base + ((claimAmount - bracket.over) * bracket.percent) / 100
      if (bracket.min !== null) raw = Math.max(raw, bracket.min)
      break
    }
  }

  if (raw === null) {
    throw new Error("Шкала госпошлины не покрывает цену иска")
  }
  if (version.max !== null) raw = Math.min(raw, version.max)

  return {
    claimAmount,
    fee: roundHalfUp(raw, 0),
    basis: version.basis,
    effectiveFrom: version.effectiveFrom,
  }
}
